<template>
  <div class="location-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">Locations</h2>
      <b-button variant="primary" @click="createLocation">
        <i class="fas fa-plus mr-2"></i>Add Location
      </b-button>
    </div>

    <CrudTable
      :columns="fields"
      :data-sources="locations"
      :pagination="paginationInfo"
      :loader="isLoading"
      list-url="/admin/locations"
      @search="handleSearch"
      @filter="handleFilter"
      @refresh="fetchLocations"
    >
      <!-- Name Column -->
      <template #cell(name)="data">
        <div class="d-flex align-items-center">
          <div>
            <div class="font-weight-bold">{{ data.item.name }}</div>
            <div class="text-muted small">{{ data.item.address || 'N/A' }}</div>
          </div>
        </div>
      </template>

      <!-- City Column -->
      <template #cell(city)="data">
        <div>
          {{ data.item.city || 'N/A' }}
        </div>
      </template>

      <!-- State Column -->
      <template #cell(state)="data">
        <div>
          {{ data.item.state || 'N/A' }}
        </div>
      </template>

      <!-- Country Column -->
      <template #cell(country)="data">
        <div>
          {{ data.item.country || 'N/A' }}
        </div>
      </template>

      <!-- Actions Column -->
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="editLocation(data.item)"
          >
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="confirmDelete(data.item)"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Delete"
      @ok="handleDelete"
      ok-variant="danger"
    >
      Are you sure you want to delete this location? This action cannot be undone.
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: 'LocationList',
  components: {
    CrudTable
  },

  data() {
    return {
      locations: [],
      isLoading: false,
      showDeleteModal: false,
      selectedLocation: null,
      currentPage: 1,
      perPage: 10,
      sortBy: 'created_at',
      sortDesc: true,

      filters: {
        search: '',
        city: null,
        state: null,
        country: null
      },
      fields: [
        { key: 'name', label: 'Location Name', sortable: true },
        { key: 'address', label: 'Address', sortable: true },
        { key: 'city', label: 'City', sortable: true },
        { key: 'state', label: 'State', sortable: true },
        { key: 'country', label: 'Country', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0
      }
    }
  },

  async created() {
    await this.fetchLocations();
  },

  methods: {
    async fetchLocations() {
      try {
        this.isLoading = true;
        const response = await request({
          method: "get",
          url: "admin/locations",
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_direction: this.sortDesc ? 'desc' : 'asc',
            search: this.filters.search || ''
          }
        });

        this.locations = response.data.data || [];
        this.paginationInfo = {
          from: response.data.meta?.from || 0,
          to: response.data.meta?.to || 0,
          total: response.data.meta?.total || 0
        };
      } catch (error) {
        console.error('Error fetching locations:', error);
        this.locations = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0
        };
      } finally {
        this.isLoading = false;
      }
    },

    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchLocations();
    },

    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchLocations();
    },

    createLocation() {
      this.$router.push('/admin/locations/create');
    },

    editLocation(location) {
      this.$router.push(`/admin/locations/${location.id}/edit`);
    },

    confirmDelete(location) {
      this.selectedLocation = location;
      this.showDeleteModal = true;
    },

    async handleDelete() {
      if (!this.selectedLocation) return;

      try {
        await request({
          method: "delete",
          url: `admin/locations/${this.selectedLocation.id}`
        });

        this.$bvToast.toast('Location deleted successfully', {
          title: 'Success',
          variant: 'success'
        });

        this.fetchLocations();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to delete location', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.showDeleteModal = false;
        this.selectedLocation = null;
      }
    }
  }
}
</script>

<style scoped>
.location-list {
  padding: 1rem;
}

.gap-2 {
  gap: 0.5rem;
}
</style> 